import { useMemo, useState } from "react";
import type { FormDefinition, FormFieldType } from "@omnabase/shared";
import { api, ApiError } from "../../../lib/api";
import { formUrl } from "../../../lib/forms";
import { addField, draftOf, duplicateField, moveField, newField, removeField, renameForm, sameDraft, saveBodyOf, type Draft, type OpenProject } from "../../../lib/formEdits";
import { draftProblems } from "../../../lib/formDraftRules";

/** The builder's state and its actions (builder v2; the rules are round one's): the form as read, the saved draft and the one being edited,
 *  which row is open (one at a time, none at first, question 12), a row to bring into view, the item whose first box takes the focus, the
 *  rename (a new form's title block follows it, the text blocks round), and Save (a save on an outdated copy is refused by the server and
 *  says so, question 7) and Cancel (back to the saved form). */
let nextUid = 0;
const uid = () => `f${++nextUid}`;

export function useFormBuilder(initial: FormDefinition, slug: string, projects: readonly OpenProject[], toast: (text: string, o: { kind: "success" | "error" }) => void) {
  const [read, setRead] = useState(initial);
  const [saved, setSaved] = useState<Draft>(() => draftOf(initial, uid));
  const [draft, setDraft] = useState<Draft>(saved);
  const [open, setOpen] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ uid: string; n: number } | null>(null); // the field whose Label takes the focus (again when n grows)
  const [reveal, setReveal] = useState<{ uid: string; n: number } | null>(null);
  const [busy, setBusy] = useState(false); const [outdated, setOutdated] = useState(false);

  const problems = useMemo(() => draftProblems(draft), [draft]);
  const show = (u: string, focusLabel = false) => { setOpen(u); setFocus(focusLabel ? { uid: u, n: 1 } : null); setReveal((r) => ({ uid: u, n: (r?.n ?? 0) + 1 })); };

  const reset = (d: FormDefinition) => { const next = draftOf(d, uid); setRead(d); setSaved(next); setDraft(next); setOpen(null); setOutdated(false); };
  const save = async () => {
    setBusy(true);
    try {
      const next = await api.patch<FormDefinition>(formUrl(slug, read.id), saveBodyOf(draft, read));
      const kept = { ...draft, name: next.name, items: draft.items.map(({ fresh: _f, ...f }) => f) }; // the uids stay (the open row holds), no key follows its label any more
      setRead(next); setSaved(kept); setDraft(kept); setOutdated(false);
      toast("Form saved", { kind: "success" });
    } catch (e) {
      if (e instanceof ApiError && (e.details as { reason?: string } | undefined)?.reason === "outdated") setOutdated(true);
      else toast(e instanceof ApiError ? e.message : "Could not save the form", { kind: "error" });
    } finally { setBusy(false); }
  };
  const reload = async () => { try { reset(await api.fresh<FormDefinition>(formUrl(slug, read.id))); } catch (e) { toast((e as Error).message, { kind: "error" }); } };

  return {
    read, saved, draft, setDraft, problems, busy, outdated, open, focus, reveal,
    dirty: !sameDraft(draft, saved),
    save, reload,
    rename: (name: string) => setDraft((d) => renameForm(d, name)),
    cancel: () => { setDraft(saved); if (open && !saved.items.some((f) => f.uid === open)) setOpen(null); },
    toggle: (u: string) => { setFocus(null); setOpen((o) => (o === u ? null : u)); },
    showField: (u: string) => show(u),
    /** the menu that added a field has closed (and let go of the focus): the new field's Label takes it again */
    refocus: () => setFocus((f) => (f ? { ...f, n: f.n + 1 } : f)),
    add: (type: FormFieldType) => { const f = newField(draft, type, uid()); setDraft((d) => addField(d, f)); show(f.uid, true); },
    duplicate: (u: string) => { const n = uid(); setDraft((d) => duplicateField(d, u, n)); show(n); },
    remove: (u: string) => { setDraft((d) => removeField(d, u)); setOpen(null); },
    move: (from: number, to: number) => setDraft((d) => moveField(d, from, to)),
  };
}
export type FormBuilderState = ReturnType<typeof useFormBuilder>;
